<template>
  <v-container>
    <v-row>
      <v-col>
        <h3 class="text-center">{{ pageTitle }}</h3>
      </v-col>
    </v-row>
    <ImportRunStatusBanner
      :chunkID="chunkID"
      :destination="destination"
      @change="onRunStatus"
    />
    <v-row>
      <v-col cols="4" align="center">
        <p class="font-weight-bold">Imported</p>
        <p>(Import vouchers)</p>
        <v-btn :disabled="true">
          DONE
        </v-btn>
      </v-col>
      <v-col cols="4" align="center">
        <p class="font-weight-bold">Mapping</p>
        <p>(Match imported vouchers)</p>
        <v-btn :disabled="true">
          DONE
        </v-btn>
      </v-col>
      <v-col cols="4" align="center">
        <p class="font-weight-bold">Completed(Process)</p>
        <p>(Mark matched vouchers paid?)</p>
        <v-btn
          id="tmc-mark-vouchers-paid"
          :disabled="isUpdated || !resultsReady"
          depressed
          color="primary"
          @click="updateMatchedTransactionStatus"
        >
          {{ isUpdated ? 'Done' : 'Proceed' }}
        </v-btn>
      </v-col>
    </v-row>
    <template v-if="resultsReady">
      <p></p>
      <hr>
      <p></p>
      <h3 class="text-center">MATCHED VOUCHERS</h3>
      <v-data-table
        :headers="transactionDetails.matchedData.headers"
        :items="transactionDetails.matchedData.data"
        :hide-default-footer="true"
        disable-pagination
        class="elevation-1"
      >
        <template slot="body.append">
          <tr class="pink--text">
              <td colspan="3">
                Total for matched vouchers:
                ${{amountFormat(transactionDetails.matchedData.totalAmount) }}
              </td>
          </tr>
        </template>
      </v-data-table>
      <p></p>
      <hr>
      <p></p>
      <h3 class="text-center">UNMATCHED VOUCHERS</h3>
      <v-data-table
        :headers="transactionDetails.unmatchedData.headers"
        :items="transactionDetails.unmatchedData.data"
        :hide-default-footer="true"
        disable-pagination
        class="elevation-1"
      >
        <template slot="body.append">
          <tr class="pink--text">
              <td colspan="3">
                Total for unmatched vouchers:
                ${{ amountFormat(transactionDetails.unmatchedData.totalAmount) }}
              </td>
          </tr>
        </template>
      </v-data-table>
    </template>
    <v-overlay :value="overlay">
      <v-progress-circular
        :size="100"
        :width="15"
        indeterminate
        color="primary"
      ></v-progress-circular>
    </v-overlay>
  </v-container>
</template>

<script>
import { mapMutations } from 'vuex';
import ImportRunStatusBanner from '../../components/bulk/ImportRunStatusBanner.vue';
import { importFileProcessedData } from '../../util/shared/tmc-global';
import { camelCaseToWords, amountFormat } from '../../util/shared/vue-global';

const initdata = () => ({
  pageTitle: '',
  // the route's :type, which is also the import destination the run marker is filed under
  profileAction: '',
  overlay: false,
  isUpdated: false,
  // the import run this page is about, from the report URL (?chunkID=...)
  chunkID: '',
  // undefined = not known yet, null = no marker for this run (imported before markers existed),
  // otherwise the marker the server keeps for the run
  runStatus: undefined,
  resultsLoaded: false,
});

export default {
  name: 'FileProcessingDetails',
  components: { ImportRunStatusBanner },
  data: () => initdata(),
  created() {
    this.profileAction = this.$route.params.type;
    this.chunkID = String(((this.$route || {}).query || {}).chunkID || '');
    this.pageTitle = `${camelCaseToWords(this.profileAction)}`;
  },
  computed: {
    destination() {
      return this.profileAction;
    },
    transactionDetails() {
      return this.$store.state.Bulk.processedFileData;
    },
    // The staged rows are whatever the import has written so far, so nothing is listed - and nothing
    // can be marked paid - until the server says the import finished.
    resultsReady() {
      return this.runStatus === null
        || (!!this.runStatus && this.runStatus.status === 'complete');
    },
    // with no chunkID in the URL the server picked the latest run, so use the one it picked
    runChunkID() {
      return this.chunkID || (this.runStatus && this.runStatus.chunkID) || '';
    },
  },
  methods: {
    ...mapMutations([
      'setPrintScreen',
    ]),
    amountFormat,
    onRunStatus(run) {
      this.runStatus = run;
      if (this.resultsReady && !this.resultsLoaded) {
        this.resultsLoaded = true;
        this.$store.dispatch(`Bulk/${importFileProcessedData[this.profileAction]}`, {
          chunkID: this.runChunkID,
        });
      }
    },
    async updateMatchedTransactionStatus() {
      if (!this.resultsReady || this.isUpdated) {
        return;
      }
      if (!this.runChunkID) {
        this.$store.dispatch('flashError', 'This import has no run id. Open the results from the import screen, or import the file again.');
        return;
      }
      if (window.confirm('Are you sure want to update all matched records?')) {
        this.overlay = true;
        // one call for the whole run: the server marks them and says how many it actually did
        const result = await this.$store.dispatch('Bulk/markBankVouchersPaid', {
          chunkID: this.runChunkID,
        });
        this.overlay = false;
        if (!result) {
          return; // the store action has already said why
        }
        const { totalCount, paidCount, failedCount } = result;
        if (totalCount === 0) {
          this.$store.dispatch('flashInfo', 'There are no matched vouchers to mark paid.');
          return;
        }
        if (failedCount > 0) {
          const vouchers = (result.failedVouchers || []).join(', ');
          this.$store.dispatch('flashError', `Marked ${paidCount} of ${totalCount} vouchers paid. ${failedCount} could not be marked${vouchers ? ` (voucher ${vouchers})` : ''}. Press Proceed to try again.`);
          return;
        }
        this.isUpdated = true;
        this.$store.dispatch('flashInfo', `Successfully marked ${paidCount} of ${totalCount} vouchers paid.`).then(() => {
          this.setPrintScreen(true);
          this.$nextTick(() => {
            window.print();
            this.$nextTick(() => {
              this.setPrintScreen(false);
            });
          });
        });
      }
    },
  },
};
</script>
